import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { takeRight, take, noop } from "lodash";
import { useOnMount, useChevronSliders, useResizeObserver } from "@ardoq/hooks";
import { createContext, useContext, useRef, useState, useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { colors, s8, s16, timeFastMs, s4, shadowS, r4 } from "@ardoq/design-tokens";
import { text2Bold, button, InlineText } from "@ardoq/typography";
import { getFocusRingCSS, Space, hideScrollbars } from "@ardoq/style-helpers";
import { Tag, BadgeDot, BadgeDotSize } from "@ardoq/status-ui";
import { ICON_SELECTOR, Icon, IconName } from "@ardoq/icons";
import { withPlainTextPopover, WithPopover } from "@ardoq/popovers";
import { BUTTON_SELECTOR, ButtonType } from "@ardoq/button";
import { DropdownOptionType, ButtonWithDropdown } from "@ardoq/dropdown-menu";
import { DropdownSize } from "@ardoq/dropdown-menu-ui";
import { ExcludeFalsy } from "@ardoq/common-helpers";
var TabsTheme = /* @__PURE__ */ ((TabsTheme2) => {
  TabsTheme2[TabsTheme2["LIGHT_WITH_UNDERLINE"] = 1] = "LIGHT_WITH_UNDERLINE";
  TabsTheme2[TabsTheme2["COLOR_FILL"] = 2] = "COLOR_FILL";
  return TabsTheme2;
})(TabsTheme || {});
const TabsContext = createContext(
  TabsTheme.LIGHT_WITH_UNDERLINE
);
const useTabsContext = () => useContext(TabsContext);
const BUTTON_VERTICAL_PADDING = "12px";
const BUTTON_STYLES_MAP = {
  [TabsTheme.LIGHT_WITH_UNDERLINE]: css`
    background: ${colors.surfaceDefault};
    color: ${colors.textSubtle};

    &::after {
      height: ${s4};
      background: ${colors.surfaceActionBrand};
    }

    &:hover {
      background: ${colors.surfaceHover};
      color: ${colors.textModerate};
    }

    &:active {
      background: ${colors.surfaceSelected};
      color: ${colors.textModerate};
    }
  `,
  [TabsTheme.COLOR_FILL]: css`
    ${text2Bold};
    background: ${colors.surfaceActionSecondary};
    border-radius: 6px; /* wrapper has 8px. Use 6px to fit snugly inside */
    padding: ${s8} ${s16};
    flex-grow: 1;
    justify-content: center;
    transition: background-color ${timeFastMs} ease;
    color: ${colors.textDefault};

    &:hover {
      background-color: ${colors.borderAction};
    }

    &:after {
      display: none;
    }
  `
};
const ACTIVE_STYLES_MAP = {
  [TabsTheme.LIGHT_WITH_UNDERLINE]: css`
    && {
      color: ${colors.textBrandLight};
    }
  `,
  [TabsTheme.COLOR_FILL]: css`
    && {
      ${text2Bold};
      background-color: ${colors.surfaceDefault};
      box-shadow: ${shadowS};
    }
  `
};
const TabButtonStyles = styled.button`
  ${button};
  display: flex;
  gap: ${s8};
  flex-shrink: 0;
  align-items: center;

  border: 0;
  cursor: pointer;
  padding: ${BUTTON_VERTICAL_PADDING} ${s16};
  border-radius: ${r4};

  position: relative;
  &:focus-visible {
    ${getFocusRingCSS(0)}
  }

  &:disabled {
    pointer-events: none;
    color: ${colors.textDisabled};
    ${({ $popoverId }) => $popoverId && css`
        pointer-events: revert;
      `}
  }

  &::after {
    transition: transform ${timeFastMs} ease;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: calc(100% - 2px);
    bottom: 0;
    content: ' ';
    position: absolute;
    display: block;
    border-radius: ${r4};
  }

  ${({ $theme }) => BUTTON_STYLES_MAP[$theme]}

  ${({ $isActive, $theme }) => $isActive && css`
      ${ACTIVE_STYLES_MAP[$theme]}
      &::after {
        transform: translateX(-50%) scaleX(1);
      }
    `}
`;
const CHEVRON_STYLES_MAP = {
  [TabsTheme.LIGHT_WITH_UNDERLINE]: css`
    && {
      border-color: ${colors.borderSubtle00};
    }
  `,
  [TabsTheme.COLOR_FILL]: css`
    && {
      border-color: ${colors.borderAction};
      padding: ${s8} 0;
    }
  `
};
const ChevronTabButtonStyles = styled(
  TabButtonStyles
)`
  z-index: 1;
  position: absolute;
  padding: ${BUTTON_VERTICAL_PADDING} 0;
  border-radius: 0;
  margin: ${s4} 0;
  transition: transform 0.2s;

  ${ICON_SELECTOR} {
    margin: 0 ${s4};
  }
  ${({ $theme }) => CHEVRON_STYLES_MAP[$theme]}

  ${({ $direction, $isVisible }) => $direction === "left" ? css`
          left: 0;
          border-right: 1px solid;
          transform: translateX(${$isVisible ? "0%" : "-100%"});
        ` : css`
          right: 0;
          border-left: 1px solid;
          transform: translateX(${$isVisible ? "0%" : "100%"});
        `}
`;
const ChevronTabButton = ({
  direction,
  isVisible,
  ...restProps
}) => {
  const theme = useTabsContext();
  return /* @__PURE__ */ jsxs(
    ChevronTabButtonStyles,
    {
      $isActive: false,
      $theme: theme,
      $direction: direction,
      disabled: !isVisible,
      $isVisible: isVisible,
      ...restProps,
      children: [
        direction === "left" && /* @__PURE__ */ jsx(Icon, { iconName: IconName.CHEVRON_LEFT }),
        direction === "right" && /* @__PURE__ */ jsx(Icon, { iconName: IconName.CHEVRON_RIGHT })
      ]
    }
  );
};
const getIconColor = (theme, isActive) => {
  if (theme === TabsTheme.COLOR_FILL) {
    return isActive ? colors.iconActionActive : colors.icon;
  }
  return void 0;
};
const TabButton = ({
  isDisabled,
  isActive,
  iconName,
  rightIconName,
  rightIconPopoverMessage,
  label,
  tag,
  tagStatusType,
  tagPopoverContent,
  statusType,
  badgeTooltipText,
  clickId,
  tabId,
  popoverId,
  ...restButtonProps
}) => {
  const theme = useTabsContext();
  return /* @__PURE__ */ jsxs(
    TabButtonStyles,
    {
      disabled: isDisabled,
      $isActive: isActive || false,
      $theme: theme,
      $popoverId: popoverId,
      "data-ardoq-popover-id": popoverId,
      "data-click-id": clickId,
      "data-tab-id": tabId,
      role: "tab",
      "aria-selected": isActive,
      ...restButtonProps,
      children: [
        iconName && /* @__PURE__ */ jsx(Icon, { color: getIconColor(theme, isActive), iconName }),
        /* @__PURE__ */ jsx(InlineText, { children: label }),
        rightIconName && /* @__PURE__ */ jsx(
          Icon,
          {
            color: getIconColor(theme, isActive),
            iconName: rightIconName,
            ...withPlainTextPopover(rightIconPopoverMessage)
          }
        ),
        tag && /* @__PURE__ */ jsx(WithPopover, { content: tagPopoverContent, children: /* @__PURE__ */ jsx(Tag, { label: `${tag}`, statusType: tagStatusType }) }),
        statusType && /* @__PURE__ */ jsx(
          BadgeDot,
          {
            color: statusType,
            size: BadgeDotSize.SMALL,
            "data-tooltip-text": badgeTooltipText
          }
        )
      ]
    }
  );
};
const TabLabelWrapper = styled(Space).attrs({ $gap: "s8" })`
  ${ICON_SELECTOR} {
    color: ${colors.iconAction};
  }
`;
const tabPropsToDropdownOption = ({
  label,
  tabId,
  clickId,
  statusType,
  badgeTooltipText,
  rightIconName,
  rightIconPopoverMessage
}, {
  onTabChange,
  activeTabId
}) => ({
  label: /* @__PURE__ */ jsxs(TabLabelWrapper, { children: [
    label,
    badgeTooltipText && /* @__PURE__ */ jsx(Tag, { label: badgeTooltipText, statusType }),
    rightIconName && /* @__PURE__ */ jsx(
      Icon,
      {
        iconName: rightIconName,
        ...withPlainTextPopover(rightIconPopoverMessage)
      }
    )
  ] }),
  name: `${tabId}`,
  clickId,
  isSelected: tabId === activeTabId,
  onClick: () => onTabChange == null ? void 0 : onTabChange(tabId),
  type: DropdownOptionType.OPTION
});
const moveItemInArray = (options, indexFrom, indexTo) => options.reduce((acc, option, index) => {
  if (indexFrom === indexTo) {
    return [...acc, option];
  } else if (index === indexFrom) {
    return acc;
  } else if (index === indexTo && indexTo > indexFrom) {
    return [...acc, option, options[indexFrom]];
  } else if (index === indexTo && indexTo < indexFrom) {
    return [...acc, options[indexFrom], option];
  }
  return [...acc, option];
}, []);
const getWidthUntilActiveTab = (indexOfActiveTab, tabs) => tabs.reduce(
  (sumWidth, { width }, index) => index > indexOfActiveTab ? sumWidth : sumWidth + width,
  0
);
const calculateTabsOrder = (activeTabId, tabs, wrapperWidth) => {
  var _a;
  const indexOfActiveTab = tabs.findIndex(({ tabId }) => tabId === activeTabId);
  const widthUntilActiveTab = getWidthUntilActiveTab(indexOfActiveTab, tabs);
  const widthOfAllComibnedTabs = tabs.reduce((sum, tab) => sum + tab.width, 0);
  const activeTabWidth = (_a = tabs[indexOfActiveTab]) == null ? void 0 : _a.width;
  if (widthOfAllComibnedTabs <= wrapperWidth) {
    return {
      sortedTabs: tabs,
      indexOfLastTabFittingToWrapper: tabs.length - 1
    };
  }
  if (activeTabWidth && wrapperWidth < activeTabWidth) {
    return {
      sortedTabs: tabs,
      indexOfLastTabFittingToWrapper: -1
    };
  }
  if (wrapperWidth >= widthUntilActiveTab) {
    const indexOfLastTabFittingToWrapper2 = (
      // find index of first overflowing tab and return one less
      tabs.findIndex(
        (_option, index) => getWidthUntilActiveTab(index, tabs) > wrapperWidth
      ) - 1
    );
    return {
      sortedTabs: tabs,
      indexOfLastTabFittingToWrapper: indexOfLastTabFittingToWrapper2
    };
  }
  const tempTabs = moveItemInArray(tabs, indexOfActiveTab, 0);
  const indexOfLastTabFittingToWrapper = (
    // find index of first overflowing tab and return one less
    tempTabs.findIndex(
      (_option, index) => getWidthUntilActiveTab(index, tempTabs) > wrapperWidth
    ) - 1
  );
  return {
    sortedTabs: moveItemInArray(
      tabs,
      indexOfActiveTab,
      indexOfLastTabFittingToWrapper
    ),
    indexOfLastTabFittingToWrapper
  };
};
const collectChildrenSizesFromElement = (element) => {
  const children = Array.from(element.childNodes);
  return children.map((child) => child.getBoundingClientRect().width);
};
const mapWrappersChildrenToXCollection = (element) => {
  const children = Array.from(element.childNodes);
  return Object.fromEntries(
    children.map((child) => {
      const tabId = child.dataset.tabId;
      const width = child.getBoundingClientRect().width;
      if (typeof tabId !== "string") {
        throw new Error("tabId not found.");
      }
      return [tabId, { tabId, width }];
    })
  );
};
const useOptionsDropdown = (options) => {
  const wrapperRef = useRef(null);
  const [xCollection, setXCollection] = useState({});
  const [elementsSizes, setSelementSizes] = useState([]);
  const [lastElementIndex, setLastElementIndex] = useState(null);
  const [sortedTabs, setSortedTabs] = useState([]);
  useOnMount(() => {
    if (!wrapperRef.current) return;
    const elementsSizes2 = collectChildrenSizesFromElement(wrapperRef.current);
    setXCollection(mapWrappersChildrenToXCollection(wrapperRef.current));
    setSelementSizes(elementsSizes2);
  });
  useEffect(() => {
    if (!wrapperRef.current) return;
    const { indexOfLastTabFittingToWrapper, sortedTabs: sortedTabs2 } = calculateTabsOrder(
      options.activeTabId,
      Object.values(xCollection),
      options.rootWrapperWidth
    );
    setSortedTabs(sortedTabs2);
    setLastElementIndex(indexOfLastTabFittingToWrapper);
  }, [options.rootWrapperWidth, options.activeTabId]);
  return {
    wrapperRef,
    elementsSizes,
    indexOfLastElementFittingIntoTheWrapper: lastElementIndex,
    sortedTabs
  };
};
const BACKGROUND_MAP = {
  [TabsTheme.LIGHT_WITH_UNDERLINE]: colors.surfaceDefault,
  [TabsTheme.COLOR_FILL]: colors.surfaceActionSecondary
};
const Wrapper = styled.div`
  overflow: hidden;
  position: relative;
  display: flex;
  background: ${({ $theme }) => BACKGROUND_MAP[$theme]};
  justify-content: ${({ $align }) => $align};

  /* Needed in order to keep the "More" button the same height
     as the tabs buttons */
  align-items: stretch;
  ${BUTTON_SELECTOR} {
    margin: ${s4} 0;
    /* Cover the case where all tabs are collapsed and only
       "More" dropdown button is visible
       setting explicit height to that button prevents the bar height drop */
    height: 48px;
  }
  ${({ $theme }) => $theme === TabsTheme.COLOR_FILL && css`
      border-radius: ${s8};
    `};
`;
const ChildrenWrapper = styled.div`
  display: flex;
  overflow: auto;
  gap: ${s4};
  padding: ${s4};
  ${hideScrollbars};
  ${({ $theme }) => $theme === TabsTheme.COLOR_FILL && css`
      width: 100%;
    `};
`;
const TabsButtonWrapperWithChevrons = ({
  children,
  theme = TabsTheme.LIGHT_WITH_UNDERLINE,
  align,
  rootWrapperWidth
}) => {
  const {
    wrapperRef,
    wrapperOnScroll,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick,
    onRightChevronClick
  } = useChevronSliders(rootWrapperWidth);
  return /* @__PURE__ */ jsx(TabsContext.Provider, { value: theme, children: /* @__PURE__ */ jsxs(Wrapper, { $theme: theme, $align: align, children: [
    /* @__PURE__ */ jsx(
      ChevronTabButton,
      {
        isVisible: isLeftChevronVisible,
        direction: "left",
        onClick: onLeftChevronClick
      }
    ),
    /* @__PURE__ */ jsx(
      ChildrenWrapper,
      {
        $theme: theme,
        role: "tablist",
        ref: wrapperRef,
        onScroll: wrapperOnScroll,
        children
      }
    ),
    /* @__PURE__ */ jsx(
      ChevronTabButton,
      {
        isVisible: isRightChevronVisible,
        direction: "right",
        onClick: onRightChevronClick
      }
    )
  ] }) });
};
const DROPDOWN_MORE_BUTTON_WIDTH = 130;
const TabsButtonWrapperWithDropdown = ({
  children,
  onTabChange,
  theme = TabsTheme.LIGHT_WITH_UNDERLINE,
  align,
  rootWrapperWidth,
  activeTabId,
  extraDropdownOptions = [],
  clickNamespace
}) => {
  const rootWrapperRef = useRef(null);
  const {
    wrapperRef: childrenWrapperRef,
    indexOfLastElementFittingIntoTheWrapper,
    sortedTabs
  } = useOptionsDropdown({
    rootWrapperWidth: rootWrapperWidth - DROPDOWN_MORE_BUTTON_WIDTH,
    activeTabId: `${activeTabId}`
  });
  const numberOfElementsFittingIntoTheWrapper = indexOfLastElementFittingIntoTheWrapper !== null ? indexOfLastElementFittingIntoTheWrapper + 1 : null;
  const [childrenToShowInTheBar, childrenMappedToDropdownOptions] = useMemo(() => {
    if (numberOfElementsFittingIntoTheWrapper === null) {
      return [children, []];
    }
    const childrenLookup = Object.fromEntries(
      children.map((child) => [child.props.tabId, child])
    );
    const sortedChildren = Object.values(sortedTabs).map(
      ({ tabId }) => childrenLookup[tabId]
    );
    const childrenToShowInTheDropdown = takeRight(
      sortedChildren,
      sortedChildren.length - numberOfElementsFittingIntoTheWrapper
    );
    const dropdownOptions = childrenToShowInTheDropdown.map(
      ({ props }) => tabPropsToDropdownOption(props, { onTabChange, activeTabId })
    );
    return [
      take(sortedChildren, numberOfElementsFittingIntoTheWrapper),
      dropdownOptions
    ];
  }, [
    children,
    numberOfElementsFittingIntoTheWrapper,
    onTabChange,
    sortedTabs,
    activeTabId
  ]);
  const moreOptions = [
    ...childrenMappedToDropdownOptions,
    extraDropdownOptions.length && childrenMappedToDropdownOptions.length ? { type: DropdownOptionType.DIVIDER } : null,
    ...extraDropdownOptions
  ].filter(ExcludeFalsy);
  return /* @__PURE__ */ jsx(TabsContext.Provider, { value: theme, children: /* @__PURE__ */ jsxs(Wrapper, { ref: rootWrapperRef, $theme: theme, $align: align, children: [
    /* @__PURE__ */ jsx(ChildrenWrapper, { $theme: theme, ref: childrenWrapperRef, children: childrenToShowInTheBar }),
    Boolean(moreOptions.length) && /* @__PURE__ */ jsx(
      ButtonWithDropdown,
      {
        options: moreOptions,
        alignMenuTo: "right",
        dropdownSize: DropdownSize.M,
        clickNamespace,
        buttonType: ButtonType.GHOST,
        children: "More"
      }
    )
  ] }) });
};
const TabsGroupHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ $overflowType }) => $overflowType === "chevrons" && css`
      flex: 0;
      width: 0;
      min-width: 100%;
    `}
  ${({ $overflowType }) => $overflowType === "dropdown" && css`
      flex: 1;
    `}
  ${({ $hasRightContent }) => $hasRightContent && css`
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    `}
`;
const TABS_GROUP_HEADER_SELECTOR = TabsGroupHeaderWrapper.toString();
const TabBody = styled.div`
  overflow: auto;
`;
const TAB_BODY_SELECTOR = TabBody.toString();
const TabsGroupHeader = ({
  theme,
  align,
  children,
  onTabChange = noop,
  overflowType = "chevrons",
  activeTabId,
  setActiveTabId,
  extraDropdownOptions,
  rightContent,
  clickNamespace
}) => {
  const rootWrapperRef = useRef(null);
  const { width: rootWrapperWidth = 0 } = useResizeObserver(rootWrapperRef);
  const TabsButtonWrapper = overflowType === "chevrons" ? TabsButtonWrapperWithChevrons : TabsButtonWrapperWithDropdown;
  const handleTabChange = (newTabId) => {
    setActiveTabId(newTabId);
    onTabChange(newTabId);
  };
  return /* @__PURE__ */ jsxs(
    TabsGroupHeaderWrapper,
    {
      ref: rootWrapperRef,
      $overflowType: overflowType,
      $hasRightContent: !!rightContent,
      "data-click-namespace": clickNamespace,
      children: [
        /* @__PURE__ */ jsx(
          TabsButtonWrapper,
          {
            theme,
            align,
            activeTabId,
            onTabChange: handleTabChange,
            rootWrapperWidth,
            extraDropdownOptions,
            clickNamespace,
            children: children.map((child) => /* @__PURE__ */ jsx(
              TabButton,
              {
                ...child.props,
                isActive: activeTabId === child.props.tabId,
                onClick: () => handleTabChange(child.props.tabId)
              },
              child.props.tabId
            ))
          }
        ),
        rightContent && /* @__PURE__ */ jsx("div", { children: rightContent })
      ]
    }
  );
};
const TabsGroup = (props) => {
  const key = props.children.map((child) => child.props.label).join("|");
  const isControlledComponent = "activeTabId" in props;
  const internalStateHandle = useState(
    props.defaultActiveTabId || props.children[0].props.tabId
  );
  const [activeTabId, setActiveTabId] = isControlledComponent ? [props.activeTabId, noop] : internalStateHandle;
  const activeChild = props.children.find(
    (child) => child.props.tabId === activeTabId
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      TabsGroupHeader,
      {
        ...props,
        activeTabId,
        setActiveTabId
      },
      key
    ),
    activeChild && activeChild.props.children && /* @__PURE__ */ jsx(TabBody, { children: activeChild.props.children })
  ] });
};
const ViewSelectorBarWrapper = styled(Space).attrs({
  $align: "stretch"
})`
  border-bottom: 1px solid ${colors.borderAction};

  & > ${BUTTON_SELECTOR} {
    margin: ${s4};
    width: 48px;
    height: auto;
    flex: 0;
  }
`;
const Tab = ({ children }) => /* @__PURE__ */ jsx(Fragment, { children });
export {
  ChevronTabButton,
  TABS_GROUP_HEADER_SELECTOR,
  TAB_BODY_SELECTOR,
  Tab,
  TabBody,
  TabButton,
  TabsGroup,
  TabsGroupHeader,
  TabsTheme,
  ViewSelectorBarWrapper
};
//# sourceMappingURL=index.js.map
